import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Heart, User as UserIcon, Edit, Gift, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import PushNotifications from '../components/PushNotifications';
import VIPSubscriptionCard from '../components/VIPSubscriptionCard';
import MyCreatedEvents from '../components/MyCreatedEvents';
import { PAYMENTS_ENABLED, REFERRALS_ENABLED } from '../config/features';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = ['Arte', 'Cultura', 'Deporte', 'Educación', 'Gastronomía', 'Música', 'Solidario', 'Tecnología', 'Turismo'];

const Profile = () => {
  const { user, loading: authLoading, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [selectedInterests, setSelectedInterests] = useState(user?.interests || []);
  const [referralData, setReferralData] = useState(null);
  const [redeemCode, setRedeemCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    loadRSVPs();
    if (REFERRALS_ENABLED) loadReferralCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  const loadReferralCode = async () => {
    try {
      const response = await axios.get(`${API}/referral/my-code`, {
        withCredentials: true
      });
      setReferralData(response.data);
    } catch (error) {
    }
  };

  const copyCode = () => {
    if (referralData?.code) {
      navigator.clipboard.writeText(referralData.code);
      setCopied(true);
      toast.success('Código copiado!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRedeem = async () => {
    if (!redeemCode.trim()) return;
    try {
      await axios.post(`${API}/referral/redeem`, { code: redeemCode.toUpperCase() }, {
        withCredentials: true
      });
      toast.success('Código canjeado!');
      setRedeemCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    }
  };

  const loadRSVPs = async () => {
    try {
      const response = await axios.get(`${API}/rsvps/my`, {
        withCredentials: true
      });
      setRsvps(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await updateProfile({ name, interests: selectedInterests });
      setEditing(false);
      toast.success('Perfil actualizado');
    } catch (error) {
      toast.error('Error al actualizar perfil');
    }
  };

  const toggleInterest = (interest) => {
    if (selectedInterests.includes(interest)) {
      setSelectedInterests(selectedInterests.filter(i => i !== interest));
    } else {
      setSelectedInterests([...selectedInterests, interest]);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <h1 className="text-4xl font-heading font-bold text-[#0A0A0A] mb-8" data-testid="profile-title">Mi Perfil</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Info */}
          <div className="lg:col-span-1">
            <Card className="bg-white/50 backdrop-blur-sm border-[#E5E5E5]">
              <CardContent className="pt-6 space-y-6">
                <div className="text-center">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-full mx-auto mb-4" />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-primary mx-auto mb-4 flex items-center justify-center">
                      <UserIcon className="w-12 h-12 text-white" />
                    </div>
                  )}
                  {!editing ? (
                    <>
                      <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-2" data-testid="user-name">{user.name}</h2>
                      <p className="text-[#737373] mb-4" data-testid="user-email">{user.email}</p>
                      <Button onClick={() => setEditing(true)} size="sm" variant="outline" className="border-[#E5E5E5]" data-testid="edit-profile-button">
                        <Edit className="w-4 h-4 mr-2" />
                        Editar perfil
                      </Button>
                    </>
                  ) : (
                    <div className="space-y-4 text-left">
                      <div>
                        <Label className="text-[#525252]">Nombre</Label>
                        <Input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="bg-white border-[#E5E5E5] text-[#0A0A0A]"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleSave} size="sm" className="bg-primary" data-testid="save-profile-button">Guardar</Button>
                        <Button onClick={() => setEditing(false)} size="sm" variant="outline" className="border-[#E5E5E5]">Cancelar</Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t border-[#E5E5E5] pt-4">
                  <p className="text-sm text-[#737373] mb-2">Rol</p>
                  <span className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm font-semibold" data-testid="user-role">
                    {user.role === 'user' ? 'Usuario' : user.role === 'organizer' ? 'Organizador' : 'Admin'}
                  </span>
                </div>

                {/* Interests */}
                <div className="border-t border-[#E5E5E5] pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-[#737373]">Intereses</p>
                    {editing && (
                      <Button onClick={() => setSelectedInterests([])} size="sm" variant="ghost" className="text-xs h-auto py-1">
                        Limpiar
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {interests.map(interest => (
                      editing ? (
                        <button
                          key={interest}
                          onClick={() => toggleInterest(interest)}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            selectedInterests.includes(interest)
                              ? 'bg-primary text-white'
                              : 'bg-[#F5F5F5] text-[#525252] hover:bg-[#E5E5E5]'
                          }`}
                        >
                          {interest}
                        </button>
                      ) : selectedInterests.includes(interest) ? (
                        <span key={interest} className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm">
                          {interest}
                        </span>
                      ) : null
                    ))}
                  </div>
                  {!editing && selectedInterests.length === 0 && (
                    <p className="text-sm text-[#737373] italic">No hay intereses seleccionados</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* My Events */}
          <div className="lg:col-span-2 space-y-6">
            {/* Referral Card (oculto mientras los referidos están desactivados) */}
            {REFERRALS_ENABLED && (
            <Card className="bg-gradient-to-br from-primary/10 to-orange-500/5 border-primary/30">
              <CardHeader>
                <CardTitle className="text-xl font-heading text-[#0A0A0A] flex items-center gap-2">
                  <Gift className="w-6 h-6 text-primary" />
                  Programa de Referidos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {referralData && (
                  <>
                    <div>
                      <p className="text-sm text-[#525252] mb-2">Tu código personal:</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-white/80 border border-[#E5E5E5] rounded-lg px-4 py-3 font-mono text-2xl text-primary font-bold tracking-wider" data-testid="referral-code">
                          {referralData.code}
                        </div>
                        <Button onClick={copyCode} variant="outline" size="icon" className="border-[#E5E5E5]" data-testid="copy-code-button">
                          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-[#737373] mt-2">
                        Compartilo con amigos y ganá beneficios cuando se registren
                      </p>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                      <span className="text-[#525252]">Personas referidas:</span>
                      <span className="text-2xl font-bold text-primary" data-testid="referrals-count">
                        {referralData.referrals_count}
                      </span>
                    </div>
                    <div className="border-t border-[#E5E5E5] pt-4">
                      <p className="text-sm text-[#525252] mb-2">¿Tenés un código de referido?</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Ej: ABC1234"
                          value={redeemCode}
                          onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                          className="bg-white border-[#E5E5E5] text-[#0A0A0A] font-mono uppercase"
                          maxLength={10}
                          data-testid="redeem-code-input"
                        />
                        <Button onClick={handleRedeem} className="bg-primary hover:bg-primary/90" data-testid="redeem-button">
                          Canjear
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            )}

            {/* Mis eventos creados (visible solo si el usuario creó al menos uno) */}
            <MyCreatedEvents />

            {/* VIP Subscription (oculto mientras los pagos están desactivados) */}
            {PAYMENTS_ENABLED && <VIPSubscriptionCard />}

            {/* Push Notifications */}
            <PushNotifications />

            <Card className="bg-white/50 backdrop-blur-sm border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-2xl font-heading text-[#0A0A0A]">Mis Eventos</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="animate-pulse bg-[#F5F5F5] h-32 rounded-lg"></div>
                    ))}
                  </div>
                ) : rsvps.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-16 h-16 text-[#737373] mx-auto mb-4" />
                    <p className="text-[#737373] mb-4">No estás inscripto en ningún evento</p>
                    <Button onClick={() => navigate('/explorar')} className="bg-primary">Explorar eventos</Button>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="rsvp-list">
                    {rsvps.map(rsvp => (
                      <div key={rsvp.rsvp_id} className="bg-white/50 border border-[#E5E5E5] rounded-lg p-4" data-testid={`rsvp-${rsvp.rsvp_id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                rsvp.payment_status === 'completed'
                                  ? 'bg-green-500/10 text-green-400'
                                  : rsvp.payment_status === 'pending'
                                  ? 'bg-yellow-500/10 text-yellow-400'
                                  : 'bg-red-500/10 text-red-400'
                              }`}>
                                {rsvp.payment_status === 'completed' ? 'Confirmado' : rsvp.payment_status === 'pending' ? 'Pendiente' : 'Fallido'}
                              </span>
                            </div>
                            <p className="text-[#525252] text-sm">Evento #{rsvp.event_id}</p>
                            <p className="text-[#737373] text-xs mt-1">
                              {format(new Date(rsvp.created_at), "d 'de' MMMM, yyyy", { locale: es })}
                            </p>
                            {rsvp.total_amount > 0 && (
                              <p className="text-[#737373] text-sm mt-2">Total: ${rsvp.total_amount}</p>
                            )}
                          </div>
                          {rsvp.qr_code && rsvp.payment_status === 'completed' && (
                            <div className="bg-white p-2 rounded">
                              <QRCodeCanvas value={rsvp.rsvp_id} size={80} />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
